import React, { useState } from 'react';
import styled from 'styled-components';
import CommonMenu from 'src/components/CommonMenu';
import { useNavigate } from 'react-router';
import { Poppins, Spacer, TextOverflowTooltip } from 'src/common';
import { ThreeDots } from 'src/components/CommonMenu/comps';
import { Tile, TileDescription, TileDescriptionContainer, TileTitle } from 'src/components/Tile';
import { Link } from 'react-router-dom';
import { MPEvents } from 'src/utils/mixpanel/types';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { ScenarioSvg } from 'src/assets/dynamicSvg';
import { ControlFrameworksListQuery, useComplianceFrameworkGaugeQuery } from 'src/graphql/_generated';
import { ComplienceChart } from 'src/components/charts';
import { useAuth } from 'src/state/auth';
import { isQueryLoading } from 'src/graphql/client';

const BottomContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

interface FrameworkLibraryCardProps {
  framework: ControlFrameworksListQuery['controlFrameworks'][number];
}

export const FrameworkLibraryCard: React.FC<FrameworkLibraryCardProps> = ({ framework }) => {
  const navigate = useNavigate();
  const { workspaceId, features } = useAuth();
  const [anchorEl, setAnchorEl] = useState<React.MouseEvent['currentTarget'] | null>(null);

  const { data, loading, networkStatus } = useComplianceFrameworkGaugeQuery({
    variables: {
      workspaceId,
      id: framework.id,
    },
    skip: !features['app.control.suggested-frameworks'],
  });
  const isloading = isQueryLoading({ loading, data, networkStatus });
  const cfData = data?.complianceFrameworks[0];

  const handleClose = () => setAnchorEl(null);

  const handleOnViewClick = () => navigate(`/control-frameworks/${framework.id}`);

  const onMenuClick = (e: React.MouseEvent<HTMLLIElement>, cb: () => void) => {
    e.stopPropagation();
    handleClose();
    cb();
  };

  return (
    <>
      <CommonMenu
        onViewClick={(e) => onMenuClick(e, () => handleOnViewClick())}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      />

      <Tile
        className="hover-shadow"
        onClick={() => {
          if (!anchorEl) {
            navigate(`/control-frameworks/${framework.id}`);
            mpEvent(MPEvents.Link, {
              source: {
                value: ['Frameworks'],
              },
              destination: { value: ['Framework'], params: { id: framework.id } },
              tags: ['FRAMEWORK'],
            });
          }
        }}
        data-cy="framework-library-card"
      >
        <div css="display: flex;justify-content: space-between;align-items: center;">
          <TextOverflowTooltip tooltipText={framework.shortname || ''}>
            <Link
              to={`/control-frameworks/${framework.id}`}
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: {
                    value: ['Frameworks'],
                  },
                  destination: { value: ['Framework'], params: { id: framework.id } },
                  tags: ['FRAMEWORK'],
                });
              }}
            >
              <TileTitle>{framework.shortname}</TileTitle>
            </Link>
          </TextOverflowTooltip>

          <ThreeDots
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
          />
        </div>
        <Spacer $px={10} />
        <TileDescriptionContainer>
          <TileDescription>
            <Poppins px={14} color="cflowerBlue">
              {framework.name}
            </Poppins>
          </TileDescription>
        </TileDescriptionContainer>

        <BottomContainer>
          <div css="display: flex;align-items: center;flex: 1;">
            <ScenarioSvg />
            <div>
              <Poppins css="margin-left: 6px;" px={14}>
                {framework.author} •{' '}
              </Poppins>
              <Poppins px={14} weight={600}>
                {framework.controlCount}{' '}
              </Poppins>
              <Poppins px={14}>{framework.controlCount === 1 ? 'control' : 'controls'}</Poppins>
            </div>
          </div>
          {features['app.control.suggested-frameworks'] && (
            <ComplienceChart
              controlsCount={cfData?.controlsCount ?? 0}
              controlsImplementedCount={cfData?.controlsImplementedCount ?? 0}
              controlsPlannedCount={cfData?.controlsPlannedCount ?? 0}
              isloading={isloading}
            />
          )}
        </BottomContainer>
      </Tile>
    </>
  );
};
